import { ErrorBoundary, Shadow, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { useBreakpointValue } from '@genial/utils';
import { RouteProp, useRoute } from '@react-navigation/native';
import { FlatList } from 'native-base';
import React, { useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { CustomerDetailStackParamList } from '../../../../../../../routes/types';
import { statementOptionQueryState } from '../../../../../../../store/customerDetail/stataments/atom';
import {
  currentAccountStatementsQuery,
  directTreasuryStatementsQuery,
  fixedIncomeStatementsQuery,
  fundStatementsQuery,
  pensionStatementsQuery,
} from '../../../../../../../store/customerDetail/stataments/selectors';
import { StatementsOptionData } from '../../../../../../../store/customerDetail/stataments/types';
import { TableHead } from '../../TableHead';
import { EmptyOrders } from '../Empty';
import { Item } from '../Item';
import { Skeleton } from '../Skeleton';

interface TableProps {
  onDataChange: (hasData: boolean) => void;
}

interface TableWrapperContainerProps {
  children: React.ReactElement;
}

const TableWrapperContainer: React.FC<TableWrapperContainerProps> = ({ children }) => (
  <Shadow level={2} borderRadius={BoxRadius.sm} testId="statements-table-shadow">
    {children}
  </Shadow>
);

export const Table: React.FC<TableProps> = ({ onDataChange }) => {
  const contentType = 'statements';

  const { params } = useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const accountNumber = params?.accountNumber;
  const statementOptionQuery = useRecoilValue(statementOptionQueryState);
  const basePadding = statementOptionQuery === 'current-account' ? 0 : Spacing['inline.nano'];

  const currentAccountStatements = useRecoilQuery(
    currentAccountStatementsQuery({
      accountNumber,
      callEndpoint: statementOptionQuery === 'current-account',
    }),
    { cachePolicy: 'no-cache' }
  );

  const directTreasuryStatements = useRecoilQuery(
    directTreasuryStatementsQuery({
      accountNumber,
      callEndpoint: statementOptionQuery === 'direct-treasury',
    }),
    { cachePolicy: 'no-cache' }
  );

  const fixedIncomeStatements = useRecoilQuery(
    fixedIncomeStatementsQuery({
      accountNumber,
      callEndpoint: statementOptionQuery === 'fixed-income',
    }),
    { cachePolicy: 'no-cache' }
  );

  const fundStatements = useRecoilQuery(
    fundStatementsQuery({
      accountNumber,
      callEndpoint: statementOptionQuery === 'fund',
    }),
    { cachePolicy: 'no-cache' }
  );

  const pensionStatements = useRecoilQuery(
    pensionStatementsQuery({
      accountNumber,
      callEndpoint: statementOptionQuery === 'pension',
    }),
    { cachePolicy: 'no-cache' }
  );

  const TableWrapper = useBreakpointValue({
    base: React.Fragment,
    sm: TableWrapperContainer,
  });

  const viewportType = useBreakpointValue({ sm: 'tablet', lg: 'desktop' });

  const queryResponse = useMemo(() => {
    return {
      'current-account': {
        ...currentAccountStatements,
        data: currentAccountStatements.data?.data?.statements ?? [],
      },
      'direct-treasury': {
        ...directTreasuryStatements,
        data: directTreasuryStatements.data?.data?.titles ?? [],
      },
      'fixed-income': {
        ...fixedIncomeStatements,
        data: fixedIncomeStatements.data?.data ?? [],
      },
      fund: {
        ...fundStatements,
        data: fundStatements.data?.data?.portfolios ?? [],
      },
      pension: {
        ...pensionStatements,
        data: pensionStatements?.data?.data ?? [],
      },
    };
  }, [
    currentAccountStatements,
    directTreasuryStatements,
    fixedIncomeStatements,
    fundStatements,
    pensionStatements,
  ]);

  useEffect(() => {
    const currentData = queryResponse[statementOptionQuery]?.data;
    onDataChange(Array.isArray(currentData) && currentData.length > 0);
  }, [queryResponse, statementOptionQuery, onDataChange]);

  const statementsListContent = useMemo(() => {
    const { data, loading, refresher, error } = queryResponse[statementOptionQuery];
    const currentAccountStyle = { mx: Spacing['inline.nano'] };
    const isCurrentAccount = statementOptionQuery === 'current-account';

    if (loading) {
      return (
        <VStack {...(isCurrentAccount && currentAccountStyle)}>
          <Skeleton viewportType={viewportType} renderCount={8} />
        </VStack>
      );
    }

    if (error) {
      return (
        <VStack {...(isCurrentAccount && currentAccountStyle)}>
          <ErrorBoundary onRefresh={refresher} />
        </VStack>
      );
    }

    return (
      <FlatList
        key={statementOptionQuery}
        data={data as unknown as StatementsOptionData[]}
        windowSize={10}
        maxToRenderPerBatch={10}
        updateCellsBatchingPeriod={5}
        keyExtractor={(_, index) => index.toString()}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={1.5}
        maxH={890}
        ListEmptyComponent={
          <EmptyOrders isLoading={loading} data={data as unknown as StatementsOptionData[]} />
        }
        renderItem={({ item }) => <Item data={item as StatementsOptionData} />}
      />
    );
  }, [queryResponse, statementOptionQuery, viewportType]);

  return (
    <VStack px={{ base: basePadding, sm: Spacing['inline.xxs'] }} py={{ base: basePadding, sm: 0 }}>
      {statementOptionQuery === 'current-account' && (
        <TableHead contentType={contentType} viewportType={viewportType} />
      )}
      <TableWrapper>{statementsListContent}</TableWrapper>
    </VStack>
  );
};
